<template>
  <div class="llm-chat-container" :style="{ height: containerHeight }">
    <!-- Config / system prompt to mirror html-llm-chat sample -->
    <div v-if="!embeddedPanel" class="llm-chat-config">
      <!-- <div class="config-column">
        <label class="config-label">System prompt</label>
        <input
          v-model="formattedSystemPrompt"
          class="config-textarea"
          disabled
          placeholder="You are a helpful assistant…"
        />
      </div> -->
<!--      <div class="config-column compact">
        <label class="config-label">max_tokens</label>
        <input
          v-model.number="maxTokens"
          type="number"
          min="1"
          class="config-input"
        />
        <label class="config-label small">session</label>
        <div class="config-pill">{{ sessionIdDisplay }}</div>
        <label class="config-label small">turns</label>
        <div class="config-pill">{{ turnCount }}</div>
      </div>-->
    </div>

    <div class="llm-chat-history" ref="history">
      <div
        v-for="(msg, index) in messages"
        :key="index"
        :class="['llm-chat-message', msg.role, { collapsed: msg.collapsed }]"
      >
        <div class="msg-header" @click="toggleCollapse(index)">
          <div class="avatar" v-if="msg.role === 'assistant'">AI</div>
          <div class="avatar user" v-else>U</div>
          <span class="msg-role-label">{{ msg.role === 'assistant' ? 'AI Agent' : 'You' }}</span>
          <span class="msg-preview" v-if="msg.collapsed">{{ previewText(msg) }}</span>
          <button
            type="button"
            class="collapse-toggle"
            :title="msg.collapsed ? 'Expand' : 'Collapse'"
            size="sm"
            style="font-size: 7px;"
          >
            <span :class="['collapse-icon', { open: !msg.collapsed }]">&#9654;</span>
          </button>
        </div>

        <div v-show="!msg.collapsed" class="bubble glass-card">
          <div v-if="msg.htmlBody" class="artifact-html-wrap">
            <iframe
              :class="['llm-chat-html-frame', { 'is-expanded': htmlFrameExpanded[htmlFrameKey(index, 'main')] }]"
              :srcdoc="iframeSrcdoc(msg.htmlBody)"
              title="Assistant HTML response"
              sandbox="allow-scripts"
            ></iframe>
            <div class="artifact-html-actions">
              <button
                type="button"
                class="html-action-btn"
                @click.stop="openHtmlInNewTab(msg.htmlBody)"
              >
                ↗ Open in new tab
              </button>
              <button
                type="button"
                class="html-action-btn"
                @click.stop="toggleHtmlFrame(htmlFrameKey(index, 'main'))"
              >
                ⤢ Toggle size
              </button>
            </div>
          </div>
          <div v-else class="text" v-html="formatText(msg.content)"></div>

          <div v-if="msg.artifacts && msg.artifacts.length" class="artifacts-wrap">
            <div
              v-for="(artifact, aIdx) in msg.artifacts"
              :key="aIdx"
              class="artifact-block"
            >
              <div class="artifact-header">
                <span class="artifact-type-icon">{{ artifactIcon(artifact.type) }}</span>
                <span class="artifact-title">{{ escapeHtml(artifactTitle(artifact)) }}</span>
                <span class="artifact-type-label">{{ escapeHtml((artifact.type || 'unknown').toLowerCase()) }}</span>
              </div>
              <template v-if="artifactType(artifact) === 'image'">
                <img
                  v-if="artifactImageSrc(artifact)"
                  class="artifact-image"
                  :src="artifactImageSrc(artifact)"
                  :alt="artifactTitle(artifact)"
                />
                <div v-else class="artifact-data">No image source found</div>
              </template>
              <template v-else-if="artifactType(artifact) === 'html'">
                <div v-if="!artifactHtmlString(artifact)" class="artifact-data">No HTML content found</div>
                <div v-else class="artifact-html-wrap">
                  <iframe
                    :class="['artifact-html-frame', { 'is-expanded': htmlFrameExpanded[htmlFrameKey(index, 'a' + aIdx)] }]"
                    :srcdoc="iframeSrcdoc(artifactHtmlString(artifact))"
                    :title="artifactTitle(artifact)"
                    sandbox="allow-scripts"
                  ></iframe>
                  <div class="artifact-html-actions">
                    <button
                      type="button"
                      class="html-action-btn"
                      @click.stop="openHtmlInNewTab(artifactHtmlString(artifact))"
                    >
                      ↗ Open in new tab
                    </button>
                    <button
                      type="button"
                      class="html-action-btn"
                      @click.stop="toggleHtmlFrame(htmlFrameKey(index, 'a' + aIdx))"
                    >
                      ⤢ Toggle size
                    </button>
                  </div>
                </div>
              </template>
              <div
                v-else-if="artifactType(artifact) === 'table'"
                class="artifact-table-outer"
                v-html="artifactTableHtml(artifact)"
              ></div>
              <div
                v-else-if="artifactIsViz(artifact)"
                class="artifact-viz-wrap"
              >
                <div
                  v-if="artifact.format === 'vega-lite' && artifact.data"
                  :id="'llm-vega-' + index + '-' + aIdx"
                  class="artifact-viz"
                ></div>
                <pre
                  v-else
                  class="artifact-code"
                >{{ artifactFallbackString(artifact) }}</pre>
              </div>
              <div
                v-else-if="artifactType(artifact) === 'text' || artifactType(artifact) === 'markdown'"
                class="artifact-text"
                v-html="formatText(artifactTextString(artifact))"
              ></div>
              <pre
                v-else-if="artifactType(artifact) === 'code'"
                class="artifact-code"
              >{{ artifactCodeString(artifact) }}</pre>
              <pre
                v-else
                class="artifact-code"
              >{{ artifactFallbackString(artifact) }}</pre>
            </div>
          </div>

          <!-- Tool calls summary from structured LLM response -->
          <div v-if="msg.toolCalls && msg.toolCalls.length" class="tool-calls">
            <div class="tool-calls-label">Tools</div>
            <ul>
              <li v-for="(tool, tIdx) in msg.toolCalls" :key="tIdx">
                <span class="tool-name">{{ tool.tool }}</span>
                <span class="tool-status">{{ tool.status }}</span>
                <span class="tool-ms">{{ tool.duration_ms }} ms</span>
              </li>
            </ul>
          </div>

          <!-- Token usage -->
          <div v-if="msg.usage" class="usage-footer">
            <span class="usage-item">
              Input: <span>{{ msg.usage.input_tokens }}</span>
            </span>
            <span class="usage-item">
              Output: <span>{{ msg.usage.output_tokens }}</span>
            </span>
            <span class="usage-item">
              Total: <span>{{ msg.usage.total_tokens }}</span>
            </span>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="llm-chat-message assistant">
        <div class="msg-header">
          <div class="avatar">AI</div>
          <span class="msg-role-label">AI Agent</span>
        </div>
        <div class="bubble">
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="llm-chat-error">
      {{ error }}
    </div>

    <form class="llm-chat-input-area" @submit.prevent="onSend">
      <div class="attach-row">
        <label class="attach-btn">
          📎 Attach file
          <input type="file" multiple @change="onFilesSelected" />
        </label>
        <!-- <label class="attach-btn">
          🖼 Attach image
          <input
            type="file"
            multiple
            accept="image/*"
            @change="onImagesSelected"
          />
        </label> -->
        <div class="file-tags">
          <span
            v-for="(file, idx) in allSelectedFiles"
            :key="idx"
            class="file-tag"
          >
            {{ file.isImage ? '🖼' : '📄' }} {{ file.file.name }}
            <button type="button" @click="removeFile(idx, file.isImage)">×</button>
          </span>
        </div>
      </div>

      <div v-if="isDataSqlEnabled" class="data-sql-row">
        <label class="config-label data-sql-label">Data SQL (optional)</label>
        <textarea
          v-model="dataSqlInput"
          class="llm-chat-input data-sql-input"
          rows="2"
          placeholder="Configured datasql id (e.g. SQL514) or a single SELECT / WITH … SELECT. Rows are fetched server-side and sent as JSON with your message (alternative to attaching CSV/JSON files)."
          @keydown.enter.exact.prevent
        ></textarea>
      </div>

      <textarea
        v-model="input"
        class="llm-chat-input"
        placeholder="Ask about your data, e.g. 'Provide a visualization for this data and analyse it.'"
        @keydown.enter.exact.prevent="onSend"
      ></textarea>

      <button
        type="submit"
        class="llm-chat-send-button pulse-btn"
        :disabled="
          isLoading ||
            (!input.trim() &&
              selectedFiles.length === 0 &&
              selectedImages.length === 0 &&
              !dataSqlInput.trim())
        "
      >
        Send
      </button>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import { loadVegaEmbedFromCdn } from '../utils/vegaEmbedCdn';

export default {
  name: 'LlmChat',
  props: {
    /** When true, hides the system-prompt block for compact embedding (e.g. side panel). */
    embeddedPanel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      htmlFrameExpanded: {},
      containerHeight: 'calc(100vh - 120px)',
      messages: [],
      systemPrompt:
        'You are a helpful AI assistant with file reading capabilities. When asked to perform calculations, use the available math tools. When a user uploads ANY file (PDF, DOCX, XLSX, PPTX, CSV, code, text, etc.), use the read_file tool to read its contents. For images, you can see them directly — describe what you see. Always use the tools available to you.',
      maxTokens: 4096,
      sessionId: 'sess_' + Date.now(),
      turnCount: 0,
      input: '',
      selectedFiles: [],
      selectedImages: [],
      dataSqlInput: '',
      isLoading: false,
      error: null,
      isDataSqlEnabled: false,
    };
  },
  mounted() {
    this.loadDefaults();
    this.$nextTick(() => {
      const el = this.$el;
      if (el) {
        const top = el.getBoundingClientRect().top;
        this.containerHeight = this.embeddedPanel
          ? 'calc(93vh - 56px)'
          : `calc(93vh - ${Math.round(top)}px)`;
      }
    });
  },
  computed: {
    sessionIdDisplay() {
      return this.sessionId.slice(-10);
    },
    allSelectedFiles() {
      return [
        ...this.selectedFiles.map((f) => ({ file: f, isImage: false })),
        ...this.selectedImages.map((f) => ({ file: f, isImage: true }))
      ];
    }
  },
  methods: {
    loadDefaults(){
      let submenuParam = this.$route.params.submenuId;
      let menuMasterData = this.$store.state.menuMasterFlat.filter(
        (m) => m.menuId === submenuParam,
      );
      let llmConfig = menuMasterData[0].llmConfig;
      if (llmConfig) {
        this.systemPrompt = llmConfig.systemPrompt;
        this.messages = llmConfig.messages;
        this.isDataSqlEnabled = llmConfig.isDataSqlEnabled;
      }
    },
    toggleCollapse(index) {
      const msg = this.messages[index];
      this.$set(this.messages, index, {
        ...msg,
        collapsed: !msg.collapsed
      });
    },
    previewText(msg) {
      if (!msg) return '';
      let raw = msg.htmlBody
        ? msg.htmlBody.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        : msg.content || '';
      if (!raw && msg.artifacts && msg.artifacts.length) {
        raw = `[${msg.artifacts.length} artifact(s)]`;
      }
      if (!raw) return '';
      const plain = raw.replace(/\*\*/g, '').replace(/`/g, '');
      return plain.length > 80 ? plain.slice(0, 80) + '...' : plain;
    },
    onFilesSelected(event) {
      this.selectedFiles = Array.from(event.target.files || []);
    },
    onImagesSelected(event) {
      this.selectedImages = Array.from(event.target.files || []);
    },
    removeFile(index, isImage) {
      if (isImage) {
        const imgIndex = index - this.selectedFiles.length;
        if (imgIndex >= 0) {
          this.selectedImages.splice(imgIndex, 1);
        }
      } else {
        this.selectedFiles.splice(index, 1);
      }
    },
    async onSend() {
      const trimmed = this.input.trim();
      const dataSql = this.dataSqlInput.trim();
      if (
        (!trimmed &&
          this.selectedFiles.length === 0 &&
          this.selectedImages.length === 0 &&
          !dataSql) ||
        this.isLoading
      ) {
        return;
      }

      this.error = null;

      this.messages.push({
        role: 'user',
        content:
          trimmed ||
          (dataSql ? '(Data SQL reference — see fetched JSON in request)' : '(see attached files)'),
        collapsed: false
      });
      this.input = '';
      this.$nextTick(this.scrollToBottom);

      this.isLoading = true;
      try {
        // Mirror html-llm-chat: POST /api/chat with multipart form containing
        // session_id, system_prompt, user_text, tools_hint, max_tokens, files, images.
        const form = new FormData();
        form.append('session_id', this.sessionId);
        form.append('system_prompt', this.systemPrompt || '');
        form.append('user_text', trimmed);
        form.append(
          'tools_hint',
          JSON.stringify([
            'add',
            'subtract',
            'multiply',
            'divide',
            'power',
            'modulo',
            'read_file',
            'get_file_info',
            'list_uploaded_files'
          ])
        );
        form.append('max_tokens', String(this.maxTokens || 4096));
        this.selectedFiles.forEach((f) => form.append('files', f));
        this.selectedImages.forEach((i) => form.append('images', i));
        if (dataSql) {
          form.append('data_sql', dataSql);
        }

        // Route through the Kotlin backend (same origin) which proxies to the Python LLM.
        // Match generous server-side read timeout so the client does not abort first (default axios is no limit).
        const res = await axios.post('/api/chat', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 620000
        });
        const data = res.data;

        this.turnCount += 1;

        const assistantMessage = this.buildAssistantMessageFromResponse(data);
        this.messages.push(assistantMessage);
        this.$nextTick(() => {
          this.scrollToBottom();
          this.renderVegaForMessage(this.messages.length - 1);
        });

        // Reset attachments after successful send
        this.selectedFiles = [];
        this.selectedImages = [];
        this.dataSqlInput = '';
      } catch (err) {
        // Basic error handling
        // eslint-disable-next-line no-console
        console.error('LLM API call failed', err);
        const d = err.response && err.response.data;
        const message =
          (d && d.message) ||
          (d && d.error) ||
          (typeof d === 'string' ? d : null) ||
          'Failed to process your request. Please try again.';
        this.error = message;
      } finally {
        this.isLoading = false;
      }
    },
    buildAssistantMessageFromResponse(data) {
      const extractText = (raw) => {
        if (!raw) return '';
        if (typeof raw === 'string') {
          const trimmed = raw.trim();
          if (trimmed.startsWith('[{') && trimmed.includes("'type'") && trimmed.includes("'text'")) {
            const texts = [];
            const regex = /'text'\s*:\s*'((?:[^'\\]|\\.|'')*?)'\s*[,}]/g;
            let match;
            while ((match = regex.exec(trimmed)) !== null) {
              const val = match[1]
                .replace(/\\n/g, '\n')
                .replace(/\\'/g, "'")
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, '\\');
              if (val !== 'text') texts.push(val);
            }
            if (texts.length) return texts.join('\n').trim();
            const dqRegex = /"text"\s*:\s*"((?:[^"\\]|\\.)*)"\s*[,}]/g;
            while ((match = dqRegex.exec(trimmed)) !== null) {
              const val = match[1]
                .replace(/\\n/g, '\n')
                .replace(/\\'/g, "'")
                .replace(/\\"/g, '"')
                .replace(/\\\\/g, '\\');
              if (val !== 'text') texts.push(val);
            }
            if (texts.length) return texts.join('\n').trim();
          }
          if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            try {
              const parsed = JSON.parse(trimmed);
              return extractText(parsed);
            } catch (e) {
              try {
                const json = trimmed
                  .replace(/'/g, '"')
                  .replace(/True/g, 'true')
                  .replace(/False/g, 'false')
                  .replace(/None/g, 'null');
                const parsed = JSON.parse(json);
                return extractText(parsed);
              } catch (e2) {
                return raw;
              }
            }
          }
          return raw;
        }
        if (Array.isArray(raw)) {
          return raw
            .filter((b) => !b.type || b.type === 'text')
            .map((b) => (typeof b === 'string' ? b : b.text || ''))
            .join('\n')
            .trim();
        }
        if (typeof raw === 'object' && raw.text) return raw.text;
        return String(raw);
      };

      const resp = data && data.response;
      const responseTextRaw = extractText(
        (resp && resp.text) || (typeof data.response === 'string' ? data.response : '') || ''
      );

      const htmlBody = this.resolveHtmlBody(resp, responseTextRaw);

      const artifacts = Array.isArray(resp && resp.artifacts) ? resp.artifacts : [];

      const hasHtmlArtifact = artifacts.some((a) => {
        const t = String((a && a.type) || '').toLowerCase();
        return t === 'html' || t === 'dashboard';
      });
      const responseText = hasHtmlArtifact
        ? this.stripHtmlFromText(responseTextRaw)
        : responseTextRaw;

      const textTrim = (responseTextRaw || '').trim();
      const htmlTrim = (htmlBody || '').trim();
      const mainPayloadIsHtmlOnly =
        !!htmlTrim &&
        (textTrim === htmlTrim ||
          /^<!DOCTYPE\s+html/i.test(textTrim) ||
          /^<html[\s>]/i.test(textTrim));

      const content = mainPayloadIsHtmlOnly
        ? ''
        : responseText ||
          (artifacts.length || htmlTrim ? '' : 'No analysis was returned.');

      return {
        role: 'assistant',
        content,
        htmlBody: htmlBody || null,
        artifacts,
        toolCalls: (data && data.tool_calls_made) || [],
        usage: data && data.usage ? data.usage : null,
        collapsed: false
      };
    },
    stripHtmlFromText(text) {
      if (!text) return '';
      let s = String(text);
      s = s.replace(/```(?:html|HTML)\s*\n[\s\S]*?```/g, '');
      s = s.replace(/<!DOCTYPE[\s\S]*?<\/html\s*>/gi, '');
      s = s.replace(/<html[\s\S]*?<\/html\s*>/gi, '');
      s = s.replace(/<head[\s\S]*?<\/head\s*>/gi, '');
      s = s.replace(/<body[\s\S]*?<\/body\s*>/gi, '');
      s = s.replace(/```\s*\n[\s\S]*?(?:<!DOCTYPE|<html)[\s\S]*?```/gi, '');
      s = s.replace(/\n{3,}/g, '\n\n').trim();
      return s;
    },
    htmlFrameKey(msgIndex, suffix) {
      return `${msgIndex}-${suffix}`;
    },
    toggleHtmlFrame(key) {
      const cur = !!this.htmlFrameExpanded[key];
      this.$set(this.htmlFrameExpanded, key, !cur);
    },
    openHtmlInNewTab(html) {
      const h = this.injectChartJsForSandboxedHtml(html != null ? String(html) : '');
      if (!h) return;
      try {
        const blob = new Blob([h], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, '_blank', 'noopener,noreferrer');
        if (w) {
          setTimeout(() => URL.revokeObjectURL(url), 120000);
        } else {
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        const w = window.open('', '_blank', 'noopener,noreferrer');
        if (w && w.document) {
          w.document.open();
          w.document.write(h);
          w.document.close();
        }
      }
    },
    renderVegaForMessage(msgIndex) {
      const msg = this.messages[msgIndex];
      if (!msg || !msg.artifacts || !msg.artifacts.length) return;
      const specs = [];
      msg.artifacts.forEach((a, aIdx) => {
        const t = String((a && a.type) || '').toLowerCase();
        if (
          (t === 'viz' || t === 'chart') &&
          a.format === 'vega-lite' &&
          a.data
        ) {
          specs.push({ aIdx, data: a.data });
        }
      });
      if (!specs.length) return;
      this.$nextTick(() => {
        loadVegaEmbedFromCdn()
          .then((vegaEmbed) => {
            specs.forEach(({ aIdx, data }) => {
              const el = document.getElementById(`llm-vega-${msgIndex}-${aIdx}`);
              if (!el) return;
              el.innerHTML = '';
              vegaEmbed(el, data, { actions: false }).catch((err) => {
                // eslint-disable-next-line no-console
                console.error('Vega-Lite embed failed', err);
              });
            });
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('Vega CDN load skipped or failed', err);
          });
      });
    },
    resolveHtmlBody(resp, responseText) {
      if (!resp && !responseText) return null;
      if (resp && typeof resp.html === 'string' && resp.html.trim()) {
        return resp.html;
      }
      if (resp && resp.format === 'html') {
        const h = resp.html || resp.text;
        if (typeof h === 'string' && h.trim()) return h;
      }
      const t = (responseText || '').trim();
      if (/^<!DOCTYPE\s+html/i.test(t) || /^<html[\s>]/i.test(t)) {
        return responseText;
      }
      return null;
    },
    /**
     * LLM HTML often loads Chart.js from a CDN; production CSP blocks those URLs, so Chart is undefined.
     * Load Chart from this app's origin (see public/vendor/Chart.min.js). Absolute URL works in srcdoc
     * and blob: pages where relative /vendor/... would fail.
     */
    injectChartJsForSandboxedHtml(html) {
      let h = String(html || '');
      const needsChart =
        /\bnew\s+Chart\s*\(/i.test(h) ||
        /\bChart\.defaults\b/i.test(h) ||
        /\bChart\.register\b/i.test(h) ||
        /chart\.js/i.test(h);
      if (!needsChart) {
        return h.replace(/<\/iframe/gi, '<\\/iframe');
      }
      let origin = '';
      try {
        origin = typeof window !== 'undefined' ? window.location.origin || '' : '';
      } catch (e) {
        origin = '';
      }
      if (!origin) {
        return h.replace(/<\/iframe/gi, '<\\/iframe');
      }
      const chartSrc = `${origin}/vendor/Chart.min.js`;
      const tag = `<script src="${chartSrc}">` + '</' + 'script>';
      h = h.replace(
        /<script[^>]*src=["'][^"']*chart[^"']*\.js[^"']*["'][^>]*>\s*<\/script>/gi,
        ''
      );
      h = h.replace(/<\/iframe/gi, '<\\/iframe');
      if (/<head[^>]*>/i.test(h)) {
        return h.replace(/<head[^>]*>/i, '$&' + tag);
      }
      if (/<html[^>]*>/i.test(h)) {
        return h.replace(/<html[^>]*>/i, '$&<head>' + tag + '</head>');
      }
      return tag + h;
    },
    iframeSrcdoc(html) {
      if (!html) return '';
      return this.injectChartJsForSandboxedHtml(String(html));
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    artifactType(a) {
      const t = String((a && a.type) || 'unknown').toLowerCase();
      if (t === 'template' || t === 'dashboard') return 'html';
      return t;
    },
    artifactIsViz(a) {
      const t = String((a && a.type) || '').toLowerCase();
      return t === 'viz' || t === 'chart';
    },
    artifactTitle(a) {
      return (a && (a.title || a.artifact_id)) || this.artifactType(a);
    },
    artifactIcon(type) {
      const t = String(type || 'unknown').toLowerCase();
      const icons = {
        image: '🖼',
        viz: '📊',
        chart: '📊',
        table: '📋',
        html: '📊',
        dashboard: '📊',
        template: '📊',
        code: '💻',
        text: '📄',
        markdown: '📄',
        json: '📋'
      };
      return icons[t] || '📦';
    },
    artifactImageSrc(a) {
      const data = a && a.data;
      if (typeof data === 'string') return data;
      if (!data || typeof data !== 'object') return '';
      if (data.url) return data.url;
      if (data.base64) {
        return `data:${data.mime_type || 'image/png'};base64,${data.base64}`;
      }
      return '';
    },
    artifactHtmlString(a) {
      const data = a && a.data;
      if (typeof data === 'string') return data;
      if (data && typeof data.html === 'string') return data.html;
      if (data && typeof data.content === 'string') return data.content;
      return '';
    },
    artifactTextString(a) {
      const data = a && a.data;
      if (typeof data === 'string') return data;
      return data ? JSON.stringify(data, null, 2) : '';
    },
    artifactCodeString(a) {
      const data = a && a.data;
      if (typeof data === 'string') return data;
      if (data && typeof data.content === 'string') return data.content;
      return JSON.stringify(data, null, 2);
    },
    artifactFallbackString(a) {
      const data = a && a.data;
      return JSON.stringify(data != null ? data : a, null, 2);
    },
    artifactTableHtml(a) {
      const data = (a && a.data) || {};
      const headers = data.headers || [];
      const rows = data.rows || [];
      if (!headers.length && !rows.length) {
        return `<pre class="artifact-code">${this.escapeHtml(
          JSON.stringify(data, null, 2)
        )}</pre>`;
      }
      const ths = headers
        .map((h) => `<th>${this.escapeHtml(String(h))}</th>`)
        .join('');
      const trs = rows
        .map((r) => {
          const cells = Array.isArray(r) ? r : Object.values(r);
          const tds = cells
            .map((v) => `<td>${this.escapeHtml(String(v ?? ''))}</td>`)
            .join('');
          return `<tr>${tds}</tr>`;
        })
        .join('');
      const thead = ths ? `<thead><tr>${ths}</tr></thead>` : '';
      return `<div class="artifact-table-wrap"><table class="artifact-table">${thead}<tbody>${trs}</tbody></table></div>`;
    },
    scrollToBottom() {
      const history = this.$refs.history;
      if (history) {
        history.scrollTop = history.scrollHeight;
      }
    },
    formatText(text) {
      if (!text) return '';
      // Escape basic HTML first
      let s = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      // Bold **text**
      s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Inline code `text`
      s = s.replace(
        /`([^`]+)`/g,
        '<code style="background:#f3f4f6;border-radius:3px;padding:1px 5px;font-family:monospace;font-size:13px;">$1</code>'
      );
      // Preserve newlines as line breaks
      s = s.replace(/\n/g, '<br />');
      return s;
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.llm-chat-container {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 24px;
  background:
    linear-gradient(
      135deg,
      #f8fafc 0%,
      #eef2ff 45%,
      #f5f3ff 100%
    );
  border: 1px solid rgba(255,255,255,0.4);
  box-shadow:
    0 10px 40px rgba(15, 23, 42, 0.08),
    0 2px 12px rgba(15, 23, 42, 0.05);
  backdrop-filter: blur(14px);
  position: relative;
}

.llm-chat-container::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top right,
    rgba(99,102,241,0.12),
    transparent 30%);
  pointer-events: none;
}

.llm-chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  scroll-behavior: smooth;
}

.llm-chat-history::-webkit-scrollbar {
  width: 8px;
}

.llm-chat-history::-webkit-scrollbar-thumb {
  background: rgba(99,102,241,0.25);
  border-radius: 999px;
}

.llm-chat-message {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
  animation: fadeInUp 0.28s ease;
}

.llm-chat-message.user {
  align-items: flex-end;
}

.llm-chat-message.assistant {
  align-items: flex-start;
}

.msg-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 6px;
}


.collapse-icon {
  display: inline-block;
  transition: transform 0.15s ease;
}

.collapse-icon.open {
  transform: rotate(90deg);
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.avatar.user {
  background: linear-gradient(135deg, #10b981, #059669);
}

.avatar:not(.user) {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.msg-role-label {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.msg-preview {
  color: #6b7280;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 320px;
}

.bubble {
  max-width: 85%;
  padding: 18px;
  border-radius: 22px;
  position: relative;
  line-height: 1.7;
  font-size: 14px;
  word-break: break-word;
  transition: all 0.25s ease;
}

.llm-chat-message.user .bubble {
  background:
    linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  border-bottom-right-radius: 6px;
  box-shadow:
    0 10px 24px rgba(37,99,235,0.18);
}

.llm-chat-message.assistant .bubble {
  background: rgba(255,255,255,0.75);
  border: 1px solid rgba(255,255,255,0.5);
  backdrop-filter: blur(14px);
  border-bottom-left-radius: 6px;
  color: #111827;
  box-shadow:
    0 10px 30px rgba(15,23,42,0.08);
}

.bubble:hover {
  transform: translateY(-2px);
}

.text {
  white-space: pre-wrap;
}

.artifacts-wrap {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.artifact-block {
  background: rgba(255,255,255,0.9);
  border-radius: 18px;
  padding: 16px;
  border: 1px solid rgba(226,232,240,0.8);
  overflow: hidden;
  box-shadow:
    0 4px 18px rgba(15,23,42,0.05);
}

.artifact-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.artifact-title {
  font-weight: 700;
  color: #1f2937;
  flex: 1;
}

.artifact-type-label {
  font-size: 11px;
  background: #eef2ff;
  color: #4338ca;
  padding: 4px 8px;
  border-radius: 999px;
}

.artifact-image {
  width: 100%;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
}

.llm-chat-html-frame,
.artifact-html-frame {
  width: 100%;
  border: none;
  border-radius: 16px;
  background: white;
  min-height: 420px;
  box-shadow:
    inset 0 0 0 1px rgba(226,232,240,0.8);
}

.artifact-html-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.html-action-btn {
  border: none;
  padding: 10px 14px;
  border-radius: 12px;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.html-action-btn:hover {
  background: #6366f1;
  color: white;
  transform: translateY(-1px);
}

.artifact-code {
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.7;
}

.artifact-table-wrap {
  overflow-x: auto;
}

.artifact-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 14px;
  overflow: hidden;
}

.artifact-table th {
  background: #eef2ff;
  color: #4338ca;
  padding: 12px;
  font-weight: 700;
  text-align: left;
}

.artifact-table td {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}

.llm-chat-input-area {
  position: sticky;
  bottom: 0;
  padding: 18px;
  background:
    rgba(255,255,255,0.72);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(226,232,240,0.7);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attach-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.attach-btn {
  position: relative;
  overflow: hidden;
  padding: 10px 14px;
  border-radius: 14px;
  background: white;
  border: 1px solid #dbeafe;
  color: #2563eb;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.attach-btn:hover {
  background: #2563eb;
  color: white;
}

.attach-btn input[type='file'] {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.file-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 12px;
  font-weight: 600;
}

.file-tag button {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
}

.llm-chat-input {
  width: 100%;
  min-height: 56px;
  max-height: 180px;
  resize: vertical;
  border-radius: 18px;
  border: 1px solid #dbeafe;
  padding: 16px;
  font-size: 15px;
  background: rgba(255,255,255,0.95);
  transition: all 0.2s ease;
}

.llm-chat-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow:
    0 0 0 4px rgba(99,102,241,0.12);
}

.llm-chat-send-button {
  align-self: flex-end;
  min-width: 140px;
  height: 52px;
  border: none;
  border-radius: 16px;
  background:
    linear-gradient(135deg, #6366f1, #4f46e5);
  color: white;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow:
    0 10px 22px rgba(79,70,229,0.25);
}

.llm-chat-send-button:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow:
    0 16px 30px rgba(79,70,229,0.32);
}

.llm-chat-send-button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.typing-indicator {
  display: flex;
  gap: 6px;
}

.typing-indicator span {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #6366f1;
  animation: typing 1.2s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.15s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.3s;
}

.llm-chat-error {
  margin: 12px;
  padding: 14px;
  border-radius: 14px;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  font-weight: 600;
}

.tool-calls,
.usage-footer {
  margin-top: 14px;
  font-size: 12px;
  opacity: 0.8;
}

@keyframes typing {
  0%,80%,100% {
    transform: scale(0.7);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* =========================
   RESPONSIVE DESIGN
========================= */

@media (max-width: 1024px) {
  .bubble {
    max-width: 92%;
  }

  .llm-chat-history {
    padding: 18px;
  }
}

@media (max-width: 768px) {
  .llm-chat-container {
    border-radius: 0;
    height: 100vh !important;
  }

  .llm-chat-history {
    padding: 14px;
  }

  .bubble {
    max-width: 100%;
    padding: 14px;
    font-size: 13px;
  }

  .msg-preview {
    display: none;
  }

  .llm-chat-send-button {
    width: 100%;
  }

  .attach-row {
    flex-direction: column;
    align-items: stretch;
  }

  .attach-btn {
    justify-content: center;
  }

  .artifact-html-frame,
  .llm-chat-html-frame {
    min-height: 300px;
  }
}

@media (max-width: 480px) {
  .avatar {
    width: 34px;
    height: 34px;
  }

  .msg-role-label {
    font-size: 12px;
  }

  .llm-chat-input {
    font-size: 14px;
  }

  .llm-chat-send-button {
    height: 48px;
  }

  .artifact-block {
    padding: 12px;
  }
}
</style>

